'use client';

import { useSyncedLocalStorage, writeSyncedLocalStorage } from '@/lib/syncedLocalStorage';

// Keep in sync with src/agent/prompts/versions.ts (no shared package — manual sync).
export const PROMPT_VERSIONS = ['v3', 'v4'] as const;
export type PromptVersion = typeof PROMPT_VERSIONS[number];
/** Mirrors MERIT_NOTE_VERSIONS in src/agent/prompts/versions.ts — the versions
 *  whose composed prompt carries the swappable merit tool note, and so can run
 *  the inline merit arm. */
export const MERIT_NOTE_VERSIONS: readonly string[] = ['v3', 'v4'];
/** null = use the server's PROMPT_VERSION env default. */
export type PromptVersionSelection = PromptVersion | null;

const PROMPT_VERSION_KEY = 'photonium_prompt_version';
const PROMPT_VERSION_CHANGED_EVENT = 'promptVersionChanged';

export const PROMPT_VERSION_LABELS: Record<PromptVersion, string> = {
  v3: 'v3 — autonomous engineer (design loop)',
  v4: 'v4 — minimal (first-principles + corpus, default)',
};

const VALID = new Set<string>(PROMPT_VERSIONS);

function readStored(): PromptVersionSelection {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(PROMPT_VERSION_KEY);
  return v && VALID.has(v) ? (v as PromptVersion) : null;
}

export function usePromptVersion() {
  const version = useSyncedLocalStorage(
    PROMPT_VERSION_KEY,
    PROMPT_VERSION_CHANGED_EVENT,
    readStored,
  );

  function setVersion(v: PromptVersionSelection): void {
    writeSyncedLocalStorage(PROMPT_VERSION_KEY, PROMPT_VERSION_CHANGED_EVENT, v);
  }

  return { version, setVersion };
}

export function getPromptVersion(): PromptVersionSelection {
  return readStored();
}
